import { useEffect, useState, useMemo } from "react";
import {
  fetchQuery,
  postBySlugQuery,
  imageBuilder,
  type Post,
} from "@/lib/sanity";
import { ArticleContent } from "@/components/ui/article/articleContent";
import { ContactCard } from "@/components/ui/contact/contactCard";

type ArticleDetailPageProps = {
  slug: string;
  category?: string;
  onNavigate?: (path: string) => void;
};

/** Vercel-style date: "Feb 3, 2026" */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Recursively count words in Portable Text / nested objects (handles blocks, children, spans). */
function countWords(obj: unknown): number {
  if (typeof obj === "string") {
    return obj.split(/\s+/).filter(Boolean).length;
  }
  if (Array.isArray(obj)) {
    return obj.reduce((sum, item) => sum + countWords(item), 0);
  }
  if (obj && typeof obj === "object") {
    return Object.values(obj).reduce((sum, v) => sum + countWords(v), 0);
  }
  return 0;
}

/** Estimate reading time (min) from body. ~200 words/min. */
function estimateReadingMinutes(body: unknown[] | null | undefined): number {
  const words = countWords(body);
  return Math.max(1, Math.ceil(words / 200));
}

export function ArticleDetailPage({ slug, category: categoryFromUrl, onNavigate }: ArticleDetailPageProps) {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchQuery<Post | null>(postBySlugQuery, { slug })
      .then((data) => {
        if (!cancelled) setPost(data ?? null);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load article");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const readingMin = useMemo(
    () => estimateReadingMinutes(post?.body),
    [post?.body],
  );

  const imageUrlForOGP = post ? imageBuilder(post.mainImage, 1200) : "";

  // Update OGP for this article (thumbnail, title, description); restore site default on unmount.
  // Must run unconditionally (before any early return) so hook count is stable.
  useEffect(() => {
    if (!post) return;
    const setContent = (sel: string, attr: "property" | "name", value: string) => {
      const el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${sel}"]`);
      if (el) el.content = value;
    };
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const defaultImage = origin ? `${origin}/assets/thumbnail.png` : "/assets/thumbnail.png";
    const image = imageUrlForOGP || defaultImage;
    const pageUrl = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";
    setContent("og:image", "property", image);
    setContent("og:title", "property", post.title ?? "");
    setContent("og:description", "property", post.excerpt ?? "");
    setContent("og:url", "property", pageUrl);
    setContent("twitter:image", "name", image);
    setContent("twitter:title", "name", post.title ?? "");
    setContent("twitter:description", "name", post.excerpt ?? "");
    const defaultTitle = "モノバンドル株式会社 | 先端技術で世界の取引を解決する。";
    const defaultDescription = "国境を越えた取引を、より速く、確実に。私たちは、テクノロジーで国際的な取引の課題を解決します。";
    return () => {
      setContent("og:image", "property", defaultImage);
      setContent("og:title", "property", defaultTitle);
      setContent("og:description", "property", defaultDescription);
      setContent("og:url", "property", origin ? `${origin}/` : "");
      setContent("twitter:image", "name", defaultImage);
      setContent("twitter:title", "name", defaultTitle);
      setContent("twitter:description", "name", defaultDescription);
    };
  }, [post, imageUrlForOGP]);

  const go = (path: string) => {
    if (onNavigate) onNavigate(path);
    else if (typeof window !== "undefined") {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-24 text-center text-muted-foreground md:px-12">
        読み込み中…
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="px-6 py-24 md:px-12">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-muted-foreground">
            {error ?? "記事が見つかりませんでした。"}
          </p>
          <button
            type="button"
            onClick={() => go("/articles")}
            className="mt-6 text-sm font-medium text-foreground underline underline-offset-4 hover:opacity-80"
          >
            ← 記事一覧へ
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = imageBuilder(post.mainImage, 1200);

  return (
    <>
      <article className="pb-24">
        {/* Breadcrumb: Vercel-style Blog / Category */}
        <header className="px-6 pt-12 pb-8 md:px-12">
          <div className="mx-auto max-w-[680px]">
            <nav className="mb-8" aria-label="Breadcrumb">
              <button
                type="button"
                onClick={() => go("/articles")}
                className="cursor-pointer text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                記事一覧
              </button>
              {(() => {
                const cat = post.categories?.[0];
                const categorySlug = cat?.slug?.current;
                if (categorySlug) {
                  return (
                    <>
                      <span className="mx-1.5 text-muted-foreground/60">/</span>
                      <button
                        type="button"
                        onClick={() => go(`/articles/${categorySlug}`)}
                        className="cursor-pointer text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {cat.title ?? categorySlug}
                      </button>
                    </>
                  );
                }
                return null;
              })()}
              <span className="mx-1.5 text-muted-foreground/60">/</span>
              <a
                href={(() => {
                  const cat = post.categories?.[0];
                  const categorySlug = cat?.slug?.current;
                  const slug = post.slug?.current ?? "";
                  return categorySlug ? `/articles/${categorySlug}/${slug}` : `/articles/${slug}`;
                })()}
                onClick={(e) => {
                  e.preventDefault();
                  const cat = post.categories?.[0];
                  const categorySlug = cat?.slug?.current;
                  const slug = post.slug?.current ?? "";
                  go(categorySlug ? `/articles/${categorySlug}/${slug}` : `/articles/${slug}`);
                }}
                className="cursor-pointer text-sm text-foreground hover:underline"
                aria-current="page"
              >
                {post.title}
              </a>
            </nav>

            <h1 className="font-serif text-4xl font-semibold leading-[1.15] tracking-tight text-foreground md:text-5xl">
              {post.title}
            </h1>

            {/* Byline: author placeholder + reading time + date (Vercel-style) */}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>{readingMin} min read</span>
              {post.publishedAt && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Lead image: full-bleed style */}
        {imageUrl && (
          <div className="px-6 md:px-12">
            <div className="mx-auto max-w-[900px]">
              <img
                src={imageUrl}
                alt={post.title}
                className="w-full rounded-xl object-cover"
              />
            </div>
          </div>
        )}

        {/* Excerpt as lead paragraph (Vercel-style) */}
        {post.excerpt && (
          <div className="mt-10 px-6 md:px-12">
            <div className="mx-auto max-w-[680px]">
              <p className="text-lg leading-relaxed text-foreground/90">{post.excerpt}</p>
            </div>
          </div>
        )}

        {/* Body */}
        <div className={post.excerpt ? "mt-8 px-6 md:px-12" : "mt-10 px-6 md:px-12"}>
          <div className="mx-auto max-w-[680px]">
            <ArticleContent value={post.body} />
          </div>
        </div>
      </article>

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-4xl">
          <ContactCard />
        </div>
      </section>
    </>
  );
}


